import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/entreprise/Chauffeur-pour-entreprise.png";
import { cityPriceFaq } from "./cityPricing";

const valbonne: ChauffeurPage = {
  slug: "valbonne",
  title: "Chauffeur privé à Valbonne – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Valbonne : transferts aéroport de Nice, Sophia Antipolis, mise à disposition et golf, 7j/7.",
  keywords: [
    "chauffeur privé valbonne",
    "vtc valbonne",
    "chauffeur valbonne",
    "vtc sophia antipolis",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Valbonne",
    subtitle:
      "Transferts aéroport de Nice, Sophia Antipolis, mise à disposition et golf d’Opio — disponible 24h/24, 7j/7.",
    keywords: [
      "VTC Valbonne",
      "chauffeur Sophia Antipolis",
      "transfert aéroport Nice",
      "chauffeur golf Opio",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Valbonne et Sophia Antipolis",
    badges: ["Sophia Antipolis", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Valbonne",
    items: [
      {
        title: "Sophia Antipolis à portée",
        description:
          "La première technopole d’Europe s’étend sur le plateau : sièges, centres R&D et parcs d’activités que le chauffeur dessert adresse par adresse.",
      },
      {
        title: "Village provençal en damier",
        description:
          "Place des Arcades, ruelles piétonnes et restaurants du bourg : dépose au plus près, sans tourner pour se garer.",
      },
      {
        title: "Aéroport de Nice en ~25 min",
        description:
          "Liaison directe par l’A8 avec suivi de vol et accueil personnalisé, dans les deux sens.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google, chauffeurs bilingues FR/EN.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Valbonne",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Valbonne ⇄ aéroport de Nice Côte d’Azur en ~25 min par l’A8, avec suivi de vol et accueil aux terminaux.",
      },
      {
        title: "Chauffeur d’affaires à Sophia Antipolis",
        text: "Accueil de cadres et délégations, enchaînement de rendez-vous multi-sites sur la technopole, facturation entreprise.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, un chauffeur dédié pour vos réunions, déjeuners et déplacements.",
      },
      {
        title: "Golf & loisirs",
        text: "Liaisons vers le golf d’Opio-Valbonne et la Grande Bastide, avec attente sur place et retour au village.",
      },
      {
        title: "Soirées & restaurants",
        text: "Dîners autour de la Place des Arcades et retours de soirée en toute sérénité.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      {
        name: "Place des Arcades",
        description: "Cœur du village provençal bâti en damier.",
      },
      {
        name: "Sophia Antipolis",
        description: "Sièges, centres R&D et parcs d’activités du plateau.",
      },
      {
        name: "Garbejaire",
        description: "Quartier résidentiel et commerces de la technopole.",
      },
      {
        name: "Golf d’Opio-Valbonne",
        description: "Parcours et club-house, à quelques minutes du village.",
      },
      {
        name: "Vallée de la Brague",
        description: "Abords naturels et hameaux de la commune.",
      },
      { name: "Aéroport de Nice", description: "~25 min par l’A8." },
    ],
  },
  generic: {
    heading: "Valbonne, village provençal au cœur de la technopole",
    subheading:
      "Entre bastide en damier et Sophia Antipolis, un chauffeur qui connaît les deux visages de la commune.",
    paragraphs: [
      "Valbonne cultive une double identité : un village du XVIe siècle bâti en damier autour de la Place des Arcades, et une part majeure de Sophia Antipolis, la première technopole d’Europe. D’un côté des ruelles piétonnes, de l’autre un plateau où sièges et centres R&D se dispersent sur plusieurs kilomètres.",
      "Sur la technopole, les adresses sont éclatées et mal desservies sans véhicule : un chauffeur qui connaît Garbejaire, les parcs d’activités et les hôtels d’affaires fait gagner un temps précieux aux cadres et aux délégations. À ~25 min de l’aéroport de Nice par l’A8, Valbonne est un point d’arrivée fréquent pour les voyageurs d’affaires.",
      "Pour les loisirs, nous assurons les liaisons vers le golf d’Opio-Valbonne, les dîners autour de la Place des Arcades et les retours de soirée, avec la même voiture disponible d’un rendez-vous à l’autre en mise à disposition.",
    ],
    bullets: [
      "Adresses Sophia Antipolis maîtrisées",
      "Suivi de vol & facturation entreprise",
      "Golf d’Opio & village provençal",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Valbonne",
    steps: [
      "Indiquez votre trajet ou vos adresses (aéroport, Sophia Antipolis, golf, village).",
      "Nous confirmons le véhicule adapté et le forfait ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Transfert direct ou journée multi-sites, selon votre programme.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Valbonne"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Valbonne ?",
      a: "Comptez environ 25 minutes par l’A8 hors heures de pointe. Nous suivons votre vol pour ajuster l’heure de prise en charge, dans les deux sens.",
    },
    {
      q: "Desservez-vous les entreprises de Sophia Antipolis ?",
      a: "Oui, nous accueillons cadres et délégations à l’aéroport puis enchaînons les rendez-vous multi-sites sur la technopole, avec facturation entreprise.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et pendant les salons ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à la clientèle internationale de la technopole.",
    },
    {
      q: "Quels véhicules proposez-vous à Valbonne ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Assurez-vous les trajets vers le golf d’Opio-Valbonne ?",
      a: "Oui, nous desservons le golf d’Opio-Valbonne et la Grande Bastide, avec attente sur place et retour au village ou à l’hôtel.",
    },
  ],
  relatedLinks: [
    {
      label: "Service de chauffeur à Sophia Antipolis",
      href: "/chauffeur/sophia-antipolis",
    },
    { label: "Réserver un transfert aéroport", href: "/chauffeur/aeroport" },
    {
      label: "Mise à disposition avec chauffeur Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "VTC à Cannes",
      href: "/chauffeur/cannes",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Valbonne",
    text: "Transfert aéroport, Sophia Antipolis ou mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Valbonne 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Valbonne : transfert aéroport de Nice (~25 min), Sophia Antipolis, mise à disposition et golf d’Opio. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/valbonne",
  },
};

export default valbonne;
